"use client";

import { useTranslations } from "next-intl";
import type { ReactElement } from "react";
import type { ConsentCategory, ConsentChoices } from "./consent-record";

/** Banner order: one row per service, so Google consent never covers Meta. */
const OPTIONAL_ROWS: readonly {
  readonly category: ConsentCategory;
  readonly key: "statistics" | "googleAds" | "meta";
}[] = [
  { category: "statistics", key: "statistics" },
  { category: "google_ads", key: "googleAds" },
  { category: "meta", key: "meta" },
];

function CategoryRow({
  title,
  description,
  control,
}: {
  readonly title: string;
  readonly description: string;
  readonly control: ReactElement;
}): ReactElement {
  return (
    <li className="hvcc-cat">
      <div className="hvcc-cat-head">
        <span className="hvcc-cat-title">{title}</span>
        {control}
      </div>
      <p className="hvcc-cat-desc">{description}</p>
    </li>
  );
}

/** Second banner layer: every optional switch starts off unless already granted. */
export function ConsentCategories({
  draft,
  onToggle,
}: {
  readonly draft: ConsentChoices;
  readonly onToggle: (category: ConsentCategory, on: boolean) => void;
}): ReactElement {
  const t = useTranslations("public.cookieConsent");
  return (
    <ul className="hvcc-cats">
      <CategoryRow
        title={t("categories.necessary.title")}
        description={t("categories.necessary.description")}
        control={<span className="hvcc-always">{t("alwaysOn")}</span>}
      />
      {OPTIONAL_ROWS.map(({ category, key }) => {
        const title: string = t(`categories.${key}.title`);
        return (
          <CategoryRow
            key={category}
            title={title}
            description={t(`categories.${key}.description`)}
            control={
              <input
                type="checkbox"
                role="switch"
                className="hvcc-switch"
                aria-label={title}
                aria-checked={draft[category]}
                checked={draft[category]}
                onChange={(event) => onToggle(category, event.target.checked)}
              />
            }
          />
        );
      })}
    </ul>
  );
}
